import RNDateTimePicker, { type DateTimePickerEvent } from "@react-native-community/datetimepicker";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { Platform, type PlatformOSType, StyleSheet, View } from "react-native";
import OutsidePressHandler from "react-native-outside-press";
import { FieldLabel, type FieldLabelProps } from "~/components/form/FieldLabel";
import { INPUT_HEIGHT, Space } from "~/constants/sizing";
import { FontHeights, LineHeights } from "~/constants/typography";
import { useTheme } from "~/providers/ThemeProvider";
import {
    formatDateString,
    formatDateTimeString,
    formatTimeString,
    type Mode,
    makePickerEvent,
    parseInput,
} from "~/util/dates";

type WebDatePickerProps = {
    value: Date;
    readonly onChange: (event: DateTimePickerEvent, selectedDate?: Date) => void;
    mode: Mode;
    maximumDate?: Date;
    minimumDate?: Date;
} & Partial<FieldLabelProps>;

type InputPlatform = "web" | "ios";

type UpdateCallback<P extends PlatformOSType = InputPlatform> = P extends "web"
    ? (event: ChangeEvent<HTMLInputElement>) => void
    : P extends "ios"
      ? (event: DateTimePickerEvent, selectedDate?: Date) => void
      : never;

export function InlineDatePicker({
    value,
    mode,
    onChange,
    maximumDate,
    minimumDate,
    ...labelProps
}: WebDatePickerProps) {
    const { inputs, dark, colours } = useTheme();
    const [current, setCurrent] = useState<Date>(value);
    const [valid, setValid] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setCurrent(value);
        setValid(false);
        setDirty(false);
    }, [value]);

    const onUpdated = Platform.select<UpdateCallback>({
        ios: (event: DateTimePickerEvent, selectedDate?: Date) => {
            if (event.type === "set" && selectedDate) {
                setCurrent(selectedDate);
                valid || setValid(true);
                dirty || setDirty(true);
            } else {
                setValid(dirty);
            }
        },
        web: (event: ChangeEvent<HTMLInputElement>) => {
            const input = event.target.value;

            if (input) {
                setCurrent(parseInput(input, mode));
                valid || setValid(true);
                dirty || setDirty(true);
            } else {
                setValid(dirty);
            }
        },
    });

    const format = useCallback(
        (date?: Date) => {
            if (!date) {
                return undefined;
            }

            switch (mode) {
                case "date":
                    return formatDateString(date);
                case "time":
                    return formatTimeString(date);
                default:
                    return formatDateTimeString(date);
            }
        },
        [mode],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies(onChange): readonly
    // biome-ignore lint/correctness/useExhaustiveDependencies(value): won't change
    const submitValue = useCallback(
        (type: "set" | "dismissed") => {
            const val = type === "set" ? current : value;

            onChange(makePickerEvent(type, val), val);
        },
        [current],
    );

    const onBlur = useCallback(() => {
        submitValue(valid ? "set" : "dismissed");
    }, [valid, submitValue]);

    return (
        <FieldLabel {...labelProps}>
            <OutsidePressHandler onOutsidePress={onBlur} style={[inputs.input, inputs.focus, styles.container]}>
                <View style={styles.fieldWrapper}>
                    {Platform.select({
                        ios: (
                            <RNDateTimePicker
                                mode={mode}
                                minimumDate={minimumDate}
                                maximumDate={maximumDate}
                                value={current}
                                onChange={onUpdated as UpdateCallback<"ios">}
                                themeVariant={dark ? "dark" : "light"}
                                textColor={colours.text.primary}
                                accentColor={colours.icon.secondary}
                                display="compact"
                            />
                        ),
                        web: (
                            <input
                                type={mode === "datetime" ? "datetime-local" : mode}
                                value={format(current)}
                                onChange={onUpdated as UpdateCallback<"web">}
                                max={format(maximumDate)}
                                min={format(minimumDate)}
                                style={{
                                    padding: 0,
                                    outline: 0,
                                    border: 0,
                                    background: "transparent",
                                    color: colours.text.primary,
                                    fontWeight: "normal",
                                    fontSize: FontHeights.base,
                                    lineHeight: LineHeights.base,
                                    height: LineHeights.base,
                                    overflow: "visible",
                                    fontFamily: "sans-serif",
                                    outlineColor: "transparent",
                                    borderColor: "transparent",
                                    minWidth: 80,
                                    flexShrink: 0,
                                }}
                            />
                        ),
                    })}
                </View>
            </OutsidePressHandler>
        </FieldLabel>
    );
}

const styles = StyleSheet.create({
    container: {
        alignItems: "stretch",
        flexGrow: 1,
        flexShrink: 0,
        maxHeight: INPUT_HEIGHT,
        position: "relative",
        paddingVertical: 0,
        paddingHorizontal: 0,
        paddingRight: Space.sm,
    },
    fieldWrapper: {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        position: "relative",
        top: 2,
        left: 2,
        right: 2,
        bottom: 2,
    },
});
